import { useEffect, useMemo } from 'react'

import { fetchPropertyRates } from 'actions/PropertyActions'
import { useAppDispatch, useAppSelector, shallowEqual } from 'hooks/reduxHooks'
import buildPropertyRatesKey from 'lib/offer/buildPropertyRatesKey'
import { isUnavailableRoomRates } from 'lib/offer/propertyTypes'

export interface AlternativeStayCandidate {
  /** ISO check-in date */
  checkIn: string
  /** ISO check-out date */
  checkOut: string
}

export type AlternativeStayOption =
  | { status: 'loading' }
  | { status: 'sold-out' }
  | { status: 'error' }
  | { status: 'available', totalPrice: number }

/** Fields shared between the `fetchPropertyRates` request and its `buildPropertyRatesKey` lookup. */
function toRatesParams(candidate: AlternativeStayCandidate, occupancies: Array<App.Occupants>) {
  return {
    checkIn: candidate.checkIn,
    checkOut: candidate.checkOut,
    occupancies,
    utmContent: undefined,
    roomTypeId: undefined,
    roomRateId: undefined,
  }
}

/**
 * Price/availability for a property room across a set of candidate alternative-date stays. Fans
 * out one accommodation-service rates request per candidate date — responses are cached in
 * `offer.propertyRates`, so already-fetched dates (including the user's selected dates) are not
 * re-requested.
 *
 * `candidates` and `occupancies` are expected to already be stable references (the caller
 * memoises `candidates`, and `occupancies` is read straight out of offer page state), so they're
 * used as-is rather than re-stabilised here.
 */
function useRoomAlternativeStayOptions(
  propertyId: string,
  propertyRoomId: string,
  candidates: Array<AlternativeStayCandidate>,
  occupancies: Array<App.Occupants>,
): Record<string, AlternativeStayOption> {
  const dispatch = useAppDispatch()

  useEffect(() => {
    for (const candidate of candidates) {
      dispatch(fetchPropertyRates(propertyId, toRatesParams(candidate, occupancies)))
    }
  }, [dispatch, propertyId, candidates, occupancies])

  const ratesKeysByCheckIn = useMemo(() => {
    const keysByCheckIn: Record<string, string> = {}
    for (const candidate of candidates) {
      keysByCheckIn[candidate.checkIn] = buildPropertyRatesKey({ propertyId, ...toRatesParams(candidate, occupancies) })
    }
    return keysByCheckIn
  }, [propertyId, candidates, occupancies])

  // Select only the cache entries for this hook's own candidate dates — `state.offer.propertyRates`
  // grows with every property/date combo fetched anywhere on the page, so selecting the whole slice
  // would re-run this hook whenever any unrelated rate fetch resolves.
  const relevantPropertyRates = useAppSelector(state => {
    const entries: Record<string, App.State['offer']['propertyRates'][string]> = {}
    for (const key of Object.values(ratesKeysByCheckIn)) {
      entries[key] = state.offer.propertyRates[key]
    }
    return entries
  }, shallowEqual)

  return useMemo(() => {
    const options: Record<string, AlternativeStayOption> = {}
    for (const candidate of candidates) {
      const entry = relevantPropertyRates[ratesKeysByCheckIn[candidate.checkIn]]
      if (!entry?.rooms) {
        options[candidate.checkIn] = entry?.error ? { status: 'error' } : { status: 'loading' }
        continue
      }

      const roomRates = entry.rooms.find(room => room.roomId === propertyRoomId)
      if (!roomRates || isUnavailableRoomRates(roomRates)) {
        options[candidate.checkIn] = { status: 'sold-out' }
        continue
      }

      const hotelOnlyPrices = roomRates.lists.hotelOnly
        .map(item => roomRates.rates[item.id]?.totals.displayPrice)
        .filter((price): price is number => typeof price === 'number')
      options[candidate.checkIn] = hotelOnlyPrices.length
        ? { status: 'available', totalPrice: Math.min(...hotelOnlyPrices) }
        : { status: 'sold-out' }
    }
    return options
  }, [propertyRoomId, ratesKeysByCheckIn, relevantPropertyRates, candidates])
}

export default useRoomAlternativeStayOptions
